import { PropsWithChildren, createContext, useContext } from 'react'

/**
 * FAQ状態の型
 * ナビゲーション時に渡される状態データ
 */
export interface FaqState {
  /** 検索クエリ */
  q?: string[]
  /** ページ番号 */
  page?: number
  /** タグ */
  tags?: string[]
  /** パンくずリストの階層情報 */
  hierarchies?: Array<{
    title: string
    link: string
  }>
}

/**
 * 共通Link Props型
 * Gatsby と React Router の共通プロパティを抽出
 */
export interface FaqLinkProps {
  to: string
  state?: FaqState
  children?: React.ReactNode
}

/**
 * Navigate関数のオプション型
 * Gatsby と React Router の共通オプションを抽出
 */
export interface FaqNavigateOptions {
  /** 履歴エントリを置き換えるかどうか */
  replace?: boolean
  /** ナビゲーション時に渡す状態 */
  state?: FaqState
}

/**
 * Navigate関数型
 */
export interface NavigateFunction {
  (to: string, options?: FaqNavigateOptions): void
  (delta: number): void
}

/**
 * ルーティングContext値
 */
export interface FaqRoutingContextValue {
  /** navigate関数 */
  navigate: NavigateFunction

  /** Linkコンポーネント */
  Link: React.ComponentType<FaqLinkProps & React.RefAttributes<HTMLAnchorElement>>
}

const FaqRoutingContext = createContext<FaqRoutingContextValue | null>(null)

/**
 * FaqRoutingProvider
 * ルーティング環境を提供する
 */
export function FaqRoutingProvider({
  children,
  ...value
}: PropsWithChildren<FaqRoutingContextValue>) {
  return <FaqRoutingContext.Provider value={value}>{children}</FaqRoutingContext.Provider>
}

/**
 * useFaqRouting
 * ルーティングContextを取得
 *
 * @throws {Error} FaqRoutingProvider外で使用された場合
 */
export function useFaqRouting(): FaqRoutingContextValue {
  const context = useContext(FaqRoutingContext)
  if (!context) {
    throw new Error('useFaqRouting must be used within FaqRoutingProvider')
  }
  return context
}
